import { useState } from 'react';
import { useAuth } from '../app/auth';
import { api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  Modal,
  Notice,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
} from '../components/ui';

/**
 * 内容管理：技术文章与待上新产品。
 *
 * 文章正文用 Markdown 编辑；官网用轻量解析器渲染，不注入 HTML。
 * 草稿不会出现在任何公开接口里，发布动作只改 status，不删除内容。
 */

interface ArticleItem {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  tags: string[];
  body: string;
  status: 'draft' | 'published';
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

interface UpcomingItem {
  id: string;
  code: string;
  name: string;
  tagline: string;
  note: string;
  icon: string;
  status: 'draft' | 'published';
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

interface Counts {
  articles: number;
  articlesPublished: number;
  upcoming: number;
  upcomingPublished: number;
}

interface PageData {
  articles: ArticleItem[];
  upcoming: UpcomingItem[];
  counts: Counts;
}

interface ArticleForm {
  slug: string;
  title: string;
  excerpt: string;
  tags: string;
  body: string;
  status: 'draft' | 'published';
}

interface UpcomingForm {
  code: string;
  name: string;
  tagline: string;
  note: string;
  icon: string;
  sortOrder: string;
  status: 'draft' | 'published';
}

const ICON_OPTIONS = [
  'clock',
  'cpu',
  'pin',
  'signal',
  'layers',
  'plug',
  'globe',
  'zap',
  'route',
  'fleet',
  'registry',
  'tracking',
  'bell',
  'shield',
  'card',
  'key',
  'database',
  'gauge',
  'wrench',
  'scale',
] as const;

const STATUS_OPTIONS = [
  { value: 'draft', label: '草稿（不公开）' },
  { value: 'published', label: '已发布' },
] as const;

function emptyArticle(): ArticleForm {
  return { slug: '', title: '', excerpt: '', tags: '', body: '', status: 'draft' };
}

function emptyUpcoming(): UpcomingForm {
  return {
    code: '',
    name: '',
    tagline: '',
    note: '',
    icon: 'clock',
    sortOrder: '0',
    status: 'draft',
  };
}

function formatTime(value: string | null): string {
  if (value === null) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', { hour12: false });
}

export function ContentPage() {
  const { can } = useAuth();
  const canManage = can('content.manage');

  const [tab, setTab] = useState<'articles' | 'upcoming'>('articles');
  const [version, setVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const [articleEditor, setArticleEditor] = useState<{
    mode: 'create' | 'edit';
    item?: ArticleItem;
  } | null>(null);
  const [articleForm, setArticleForm] = useState<ArticleForm>(emptyArticle());
  const [upcomingEditor, setUpcomingEditor] = useState<{
    mode: 'create' | 'edit';
    item?: UpcomingItem;
  } | null>(null);
  const [upcomingForm, setUpcomingForm] = useState<UpcomingForm>(emptyUpcoming());
  const [pendingArticleDelete, setPendingArticleDelete] = useState<ArticleItem | null>(null);
  const [pendingUpcomingDelete, setPendingUpcomingDelete] = useState<UpcomingItem | null>(null);
  const [rowBusy, setRowBusy] = useState<string | null>(null);

  const state = useAsync<PageData>(async () => {
    const [articles, upcoming] = await Promise.all([
      api.get<{ items: ArticleItem[]; counts: Counts }>('/api/content/articles'),
      api.get<{ items: UpcomingItem[]; counts: Counts }>('/api/content/upcoming'),
    ]);
    return {
      articles: articles.items,
      upcoming: upcoming.items,
      counts: upcoming.counts,
    };
  }, [version]);

  function openArticleCreate() {
    setArticleEditor({ mode: 'create' });
    setArticleForm(emptyArticle());
    setError(null);
  }

  function openArticleEdit(item: ArticleItem) {
    setArticleEditor({ mode: 'edit', item });
    setArticleForm({
      slug: item.slug,
      title: item.title,
      excerpt: item.excerpt,
      tags: item.tags.join(', '),
      body: item.body,
      status: item.status,
    });
    setError(null);
  }

  async function saveArticle() {
    if (!articleEditor) return;
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      const payload = {
        slug: articleForm.slug,
        title: articleForm.title,
        excerpt: articleForm.excerpt,
        tags: articleForm.tags
          .split(/[\n,，、]/)
          .map((tag) => tag.trim())
          .filter(Boolean),
        body: articleForm.body,
        status: articleForm.status,
      };
      if (articleEditor.mode === 'create') {
        await api.post('/api/content/articles', payload);
      } else if (articleEditor.item) {
        await api.patch(
          `/api/content/articles/${encodeURIComponent(articleEditor.item.id)}`,
          payload,
        );
      }
      setArticleEditor(null);
      setSaved('技术文章已保存');
      setVersion((value) => value + 1);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setSaving(false);
    }
  }

  function openUpcomingCreate() {
    setUpcomingEditor({ mode: 'create' });
    setUpcomingForm(emptyUpcoming());
    setError(null);
  }

  function openUpcomingEdit(item: UpcomingItem) {
    setUpcomingEditor({ mode: 'edit', item });
    setUpcomingForm({
      code: item.code,
      name: item.name,
      tagline: item.tagline,
      note: item.note,
      icon: item.icon,
      sortOrder: String(item.sortOrder),
      status: item.status,
    });
    setError(null);
  }

  async function saveUpcoming() {
    if (!upcomingEditor) return;
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      const payload = {
        code: upcomingForm.code,
        name: upcomingForm.name,
        tagline: upcomingForm.tagline,
        note: upcomingForm.note,
        icon: upcomingForm.icon,
        sortOrder: Number(upcomingForm.sortOrder),
        status: upcomingForm.status,
      };
      if (upcomingEditor.mode === 'create') {
        await api.post('/api/content/upcoming', payload);
      } else if (upcomingEditor.item) {
        await api.patch(
          `/api/content/upcoming/${encodeURIComponent(upcomingEditor.item.id)}`,
          payload,
        );
      }
      setUpcomingEditor(null);
      setSaved('待上新产品已保存');
      setVersion((value) => value + 1);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setSaving(false);
    }
  }

  async function removeArticle(item: ArticleItem) {
    setRowBusy(item.id);
    try {
      await api.delete(`/api/content/articles/${encodeURIComponent(item.id)}`);
      setPendingArticleDelete(null);
      setSaved('文章已删除');
      setVersion((value) => value + 1);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setRowBusy(null);
    }
  }

  async function removeUpcoming(item: UpcomingItem) {
    setRowBusy(item.id);
    try {
      await api.delete(`/api/content/upcoming/${encodeURIComponent(item.id)}`);
      setPendingUpcomingDelete(null);
      setSaved('产品已删除');
      setVersion((value) => value + 1);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setRowBusy(null);
    }
  }

  const counts = state.data?.counts;

  return (
    <div className="space-y-5">
      <PageHeader
        title="内容管理"
        description="维护官网技术文章与「即将上线」产品。草稿不会出现在官网，发布后最多 60 秒生效。"
        action={
          <Button onClick={() => setVersion((value) => value + 1)} loading={state.loading}>
            刷新
          </Button>
        }
      />

      {!canManage ? (
        <Notice tone="info">
          当前角色只能查看内容。新增、编辑与发布需要「编辑技术文章与待上新产品」权限。
        </Notice>
      ) : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {saved ? <Notice tone="ok">{saved}</Notice> : null}
      {state.error ? <Notice tone="danger">{describeError(state.error)}</Notice> : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={tab === 'articles' ? 'primary' : 'secondary'}
          onClick={() => setTab('articles')}
        >
          技术文章 {counts ? `(${counts.articlesPublished}/${counts.articles})` : ''}
        </Button>
        <Button
          variant={tab === 'upcoming' ? 'primary' : 'secondary'}
          onClick={() => setTab('upcoming')}
        >
          待上新产品 {counts ? `(${counts.upcomingPublished}/${counts.upcoming})` : ''}
        </Button>
      </div>

      {state.loading && state.data === null ? (
        <Skeleton className="h-64 w-full" />
      ) : tab === 'articles' ? (
        <Card>
          <CardHeader
            title="技术文章"
            hint="正文使用 Markdown：## 标题、- 列表、代码块与表格会在官网渲染。"
            action={
              <Button variant="primary" onClick={openArticleCreate} disabled={!canManage}>
                新增文章
              </Button>
            }
          />
          {(state.data?.articles.length ?? 0) === 0 ? (
            <p className="py-8 text-center text-[13px] text-fg-muted">还没有技术文章。</p>
          ) : (
            <ul className="divide-y divide-line">
              {(state.data?.articles ?? []).map((item) => (
                <li key={item.id} className="flex items-start justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[13px] font-medium text-fg">{item.title}</span>
                      <Badge tone={item.status === 'published' ? 'ok' : 'warn'}>
                        {item.status === 'published' ? '已发布' : '草稿'}
                      </Badge>
                      {item.tags.map((tag) => (
                        <Badge key={tag} tone="brand">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <p className="mt-1 font-mono text-[11px] text-fg-subtle">/{item.slug}</p>
                    <p className="mt-1 line-clamp-2 text-xs text-fg-muted">{item.excerpt}</p>
                    <p className="mt-1 text-[11px] text-fg-subtle">
                      发布：{formatTime(item.publishedAt)} · 更新：{formatTime(item.updatedAt)} ·{' '}
                      {item.updatedBy}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button size="sm" onClick={() => openArticleEdit(item)} disabled={!canManage}>
                      编辑
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => setPendingArticleDelete(item)}
                      disabled={!canManage || rowBusy === item.id}
                    >
                      删除
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : (
        <Card>
          <CardHeader
            title="待上新产品"
            hint="发布后会出现在官网「即将上线」分类，按排序值从小到大展示。"
            action={
              <Button variant="primary" onClick={openUpcomingCreate} disabled={!canManage}>
                新增产品
              </Button>
            }
          />
          {(state.data?.upcoming.length ?? 0) === 0 ? (
            <p className="py-8 text-center text-[13px] text-fg-muted">还没有待上新产品。</p>
          ) : (
            <ul className="divide-y divide-line">
              {(state.data?.upcoming ?? []).map((item) => (
                <li key={item.id} className="flex items-start justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[13px] font-medium text-fg">{item.name}</span>
                      <Badge tone={item.status === 'published' ? 'ok' : 'warn'}>
                        {item.status === 'published' ? '已发布' : '草稿'}
                      </Badge>
                      <Badge tone="neutral">排序 {item.sortOrder}</Badge>
                      <Badge tone="brand">图标 {item.icon}</Badge>
                    </div>
                    <p className="mt-1 font-mono text-[11px] text-fg-subtle">{item.code}</p>
                    <p className="mt-1 text-xs text-fg-muted">{item.tagline}</p>
                    <p className="mt-1 text-[11px] text-fg-subtle">
                      更新：{formatTime(item.updatedAt)} · {item.updatedBy}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Button size="sm" onClick={() => openUpcomingEdit(item)} disabled={!canManage}>
                      编辑
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => setPendingUpcomingDelete(item)}
                      disabled={!canManage || rowBusy === item.id}
                    >
                      删除
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <Modal
        open={articleEditor !== null}
        title={articleEditor?.mode === 'edit' ? '编辑技术文章' : '新增技术文章'}
        description="正文支持 Markdown：## 标题、### 小标题、- 列表、> 引用、```代码块``` 与表格。"
        width="max-w-3xl"
        onClose={() => setArticleEditor(null)}
        footer={
          <>
            <Button onClick={() => setArticleEditor(null)}>取消</Button>
            <Button variant="primary" onClick={saveArticle} loading={saving}>
              保存
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="标题" htmlFor="article-title">
              <Input
                id="article-title"
                value={articleForm.title}
                onChange={(value) => setArticleForm({ ...articleForm, title: value })}
                maxLength={120}
              />
            </Field>
            <Field label="Slug" htmlFor="article-slug" hint="小写字母、数字与连字符">
              <Input
                id="article-slug"
                value={articleForm.slug}
                onChange={(value) => setArticleForm({ ...articleForm, slug: value })}
                maxLength={80}
              />
            </Field>
          </div>
          <Field label="摘要" htmlFor="article-excerpt" hint="最多 300 字">
            <Textarea
              id="article-excerpt"
              value={articleForm.excerpt}
              onChange={(value) => setArticleForm({ ...articleForm, excerpt: value })}
              rows={2}
              maxLength={300}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="标签" htmlFor="article-tags" hint="逗号分隔，最多 5 个">
              <Input
                id="article-tags"
                value={articleForm.tags}
                onChange={(value) => setArticleForm({ ...articleForm, tags: value })}
              />
            </Field>
            <Field label="状态" htmlFor="article-status">
              <Select
                id="article-status"
                value={articleForm.status}
                onChange={(value) => setArticleForm({ ...articleForm, status: value })}
                options={STATUS_OPTIONS.map((option) => ({ ...option }))}
              />
            </Field>
          </div>
          <Field label="正文（Markdown）" htmlFor="article-body">
            <Textarea
              id="article-body"
              value={articleForm.body}
              onChange={(value) => setArticleForm({ ...articleForm, body: value })}
              rows={18}
              maxLength={100_000}
            />
          </Field>
        </div>
      </Modal>

      <Modal
        open={upcomingEditor !== null}
        title={upcomingEditor?.mode === 'edit' ? '编辑待上新产品' : '新增待上新产品'}
        description="发布后会进入官网「即将上线」分类；不提供价格与购买入口。"
        onClose={() => setUpcomingEditor(null)}
        footer={
          <>
            <Button onClick={() => setUpcomingEditor(null)}>取消</Button>
            <Button variant="primary" onClick={saveUpcoming} loading={saving}>
              保存
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="产品名称" htmlFor="upcoming-name">
              <Input
                id="upcoming-name"
                value={upcomingForm.name}
                onChange={(value) => setUpcomingForm({ ...upcomingForm, name: value })}
                maxLength={80}
              />
            </Field>
            <Field label="产品代号" htmlFor="upcoming-code" hint="小写字母、数字与连字符">
              <Input
                id="upcoming-code"
                value={upcomingForm.code}
                onChange={(value) => setUpcomingForm({ ...upcomingForm, code: value })}
                maxLength={80}
              />
            </Field>
          </div>
          <Field label="一行描述" htmlFor="upcoming-tagline" hint="最多 160 字">
            <Input
              id="upcoming-tagline"
              value={upcomingForm.tagline}
              onChange={(value) => setUpcomingForm({ ...upcomingForm, tagline: value })}
              maxLength={160}
            />
          </Field>
          <Field label="详细说明" htmlFor="upcoming-note" hint="最多 2000 字">
            <Textarea
              id="upcoming-note"
              value={upcomingForm.note}
              onChange={(value) => setUpcomingForm({ ...upcomingForm, note: value })}
              rows={6}
              maxLength={2_000}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="图标" htmlFor="upcoming-icon">
              <Select
                id="upcoming-icon"
                value={upcomingForm.icon}
                onChange={(value) => setUpcomingForm({ ...upcomingForm, icon: value })}
                options={ICON_OPTIONS.map((icon) => ({ value: icon, label: icon }))}
              />
            </Field>
            <Field label="排序值" htmlFor="upcoming-sort" hint="越小越靠前">
              <Input
                id="upcoming-sort"
                type="number"
                value={upcomingForm.sortOrder}
                onChange={(value) => setUpcomingForm({ ...upcomingForm, sortOrder: value })}
              />
            </Field>
            <Field label="状态" htmlFor="upcoming-status">
              <Select
                id="upcoming-status"
                value={upcomingForm.status}
                onChange={(value) => setUpcomingForm({ ...upcomingForm, status: value })}
                options={STATUS_OPTIONS.map((option) => ({ ...option }))}
              />
            </Field>
          </div>
        </div>
      </Modal>

      <Modal
        open={pendingArticleDelete !== null}
        title="确认删除技术文章？"
        description="删除后无法恢复。若只是暂时下线，建议把状态改为草稿。"
        onClose={() => setPendingArticleDelete(null)}
        footer={
          <>
            <Button onClick={() => setPendingArticleDelete(null)}>取消</Button>
            <Button
              variant="danger"
              loading={pendingArticleDelete !== null && rowBusy === pendingArticleDelete.id}
              onClick={() => {
                if (pendingArticleDelete) void removeArticle(pendingArticleDelete);
              }}
            >
              删除
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-fg">{pendingArticleDelete?.title}</p>
      </Modal>

      <Modal
        open={pendingUpcomingDelete !== null}
        title="确认删除待上新产品？"
        description="删除后官网「即将上线」分类将不再展示它。"
        onClose={() => setPendingUpcomingDelete(null)}
        footer={
          <>
            <Button onClick={() => setPendingUpcomingDelete(null)}>取消</Button>
            <Button
              variant="danger"
              loading={pendingUpcomingDelete !== null && rowBusy === pendingUpcomingDelete.id}
              onClick={() => {
                if (pendingUpcomingDelete) void removeUpcoming(pendingUpcomingDelete);
              }}
            >
              删除
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-fg">{pendingUpcomingDelete?.name}</p>
      </Modal>
    </div>
  );
}
